import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, HostListener, inject, OnInit, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { CHAT_MAX_BODY_LENGTH, ChatPerson } from '../../models/chat.models';
import { ChatStore } from '../../services/chat.store';
import { ChatAvatarComponent } from '../chat-avatar/chat-avatar.component';

/**
 * "Quick Message" — Master Admin only. Pick any number of colleagues, or all of them, and post one
 * message into each of their DMs.
 *
 * Deliberately not Create Group: there is no name, no persistent conversation left behind in
 * everyone's sidebar, and it reaches someone whether or not they are currently online. Opened via
 * {@link ChatStore.beginQuickMessage} and driven entirely off store state (`quickMessageOpen`,
 * `quickMessagePreset`) rather than `@Input`s, the same way the forward dialog is — there is no
 * single parent that owns this dialog, since both the full chat page and the floating launcher can
 * open it.
 */
@Component({
  selector: 'app-chat-quick-message-dialog',
  standalone: true,
  imports: [CommonModule, FormsModule, ChatAvatarComponent],
  templateUrl: './chat-quick-message-dialog.component.html',
  styleUrls: ['./chat-quick-message-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ChatQuickMessageDialogComponent implements OnInit {
  readonly store = inject(ChatStore);
  readonly maxLength = CHAT_MAX_BODY_LENGTH;

  readonly search = signal('');
  private readonly selectedIds = signal(new Set<string>());
  body = '';

  readonly people = computed<ChatPerson[]>(() => this.store.broadcastablePeople());

  readonly filtered = computed<ChatPerson[]>(() => {
    const term = this.search().trim().toLowerCase();
    const people = this.people();
    return term ? people.filter(person => person.name.toLowerCase().includes(term)) : people;
  });

  readonly selected = computed<ChatPerson[]>(() => {
    const ids = this.selectedIds();
    return this.people().filter(person => ids.has(person.user_id));
  });

  readonly selectedCount = computed(() => this.selectedIds().size);

  readonly allSelected = computed(() => {
    const people = this.people();
    return people.length > 0 && people.every(person => this.selectedIds().has(person.user_id));
  });

  ngOnInit(): void {
    this.selectedIds.set(new Set(this.store.quickMessagePreset()));
  }

  isSelected(person: ChatPerson): boolean {
    return this.selectedIds().has(person.user_id);
  }

  toggle(person: ChatPerson): void {
    this.selectedIds.update(ids => {
      // A new Set each time: signals compare by reference, and mutating in place would not notify.
      const next = new Set(ids);
      if (!next.delete(person.user_id)) {
        next.add(person.user_id);
      }
      return next;
    });
  }

  /** Selects everyone currently loaded — offline colleagues included — or clears the picker. */
  toggleAll(): void {
    this.selectedIds.set(this.allSelected() ? new Set() : new Set(this.people().map(person => person.user_id)));
  }

  send(): void {
    const text = this.body.trim();
    const ids = Array.from(this.selectedIds());
    if (!text || !ids.length || this.store.quickMessageSending()) {
      return;
    }
    this.store.sendQuickMessage(text, ids);
  }

  close(): void {
    this.store.cancelQuickMessage();
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    this.close();
  }

  trackByPerson = (_: number, person: ChatPerson) => person.user_id;
}
